/**
 * FounderCard — Editorial founder presence used on Landing and inside articles.
 *
 * Two layout modes:
 *  - `full`    Large landscape photo + accordion bio. First 1–2 lines are
 *              always visible; "Les hele historien" expands the rest with a
 *              smooth GSAP height tween. Used on Landing + /verkstedet.
 *  - `compact` Small round portrait + abbreviated bio. Used by AuthorCard at
 *              the top of articles.
 *
 * Photo source: /images/founders/shahadil-oslo.webp. If the asset fails to
 * load (e.g. before the file is dropped), we swap to a teal initials tile
 * with "SR" so the build still passes and the section never renders empty.
 *
 * Accordion motion:
 *  - Measured-height tween (no max-height hack — we read the actual scroll
 *    height on each toggle, so the bio can grow as content changes).
 *  - Duration 450ms, ease power2.out on open / power2.in on close.
 *  - prefers-reduced-motion: instant snap, no tween.
 */

import { useEffect, useRef, useState } from 'react';
import { gsapWithScrollTrigger, motionEnabled, DURATION, EASE } from '../../lib/motion';

export type FounderCardVariant = 'full' | 'compact';

export interface FounderCardProps {
  lang: 'no' | 'en';
  variant?: FounderCardVariant;
}

const PHOTO_SRC = '/images/founders/shahadil-oslo.webp';

const COPY = {
  no: {
    eyebrow: 'Om grunnleggeren',
    name: 'Shah Adil Reyaz',
    title: 'Grunnlegger, Good Atoms',
    location: 'Oslo, Norge',
    photoAlt: 'Shah Adil Reyaz, grunnlegger av Good Atoms, fotografert i Oslo',
    expand: 'Les hele historien',
    collapse: 'Vis mindre',
    paragraphs: [
      'Jeg vokste opp i Jamshedpur i India, på en katolsk klosterskole drevet av irske nonner og et britisk-inspirert pensum. Et system som forventet svar. Senere bodde jeg fire år i Finland — verdens mest beundrede skolesystem — som heller spurte spørsmål. Siden 2018 bygger jeg liv her i Norge, sammen med sønnen min Ayaan.',
      'Tre kontinenter, tre måter å se et barn på.',
      // Sagan quote stays in English per spec.
      { type: 'mixed' as const, segments: [
        { text: 'Ayaan stiller spørsmål Carl Sagan beskrev for et halvt århundre siden: ' },
        { text: '"Children are natural scientists."', italic: true },
        { text: ' Hvorfor er himmelen blå? Hvor blir regnet av etter at det forsvinner? Hva er det inni en sky? Femti spørsmål om dagen. De fleste hadde jeg ikke gode svar på.' },
      ]},
      'Jeg startet Good Atoms fordi jeg ville ha noe å rekke ham — noe som hverken var enda et videoklipp eller en lekse, men en ekte samtale som åpnet en dør i hodet hans.',
      { type: 'mixed' as const, segments: [
        { text: 'Det norske systemet bygger frie, trygge barn. Det er en gave verden ofte misforstår. Men skoledagen slutter klokka 14, og barn slutter ikke å undre seg da. Good Atoms er for de timene mellom — for barna som spør ' },
        { text: 'hvorfor', italic: true },
        { text: ', og foreldrene som vil ha noe meningsfullt å bygge sammen med dem.' },
      ]},
    ],
    compactBio: 'Grunnlegger av Good Atoms. Skriver fra Oslo, sammen med sønnen sin Ayaan og femti spørsmål om dagen.',
  },
  en: {
    eyebrow: 'About the founder',
    name: 'Shah Adil Reyaz',
    title: 'Founder, Good Atoms',
    location: 'Oslo, Norway',
    photoAlt: 'Shah Adil Reyaz, founder of Good Atoms, photographed in Oslo',
    expand: 'Read the full story',
    collapse: 'Show less',
    paragraphs: [
      'I grew up in Jamshedpur, India, at a Catholic convent school run by Irish nuns on a British-inspired curriculum. A system that expected answers. Later I spent four years in Finland — the school system the world most admires — which asked questions instead. Since 2018 I have been building a life here in Norway, together with my son Ayaan.',
      'Three continents, three ways of seeing a child.',
      { type: 'mixed' as const, segments: [
        { text: 'Ayaan asks the questions Carl Sagan described half a century ago: ' },
        { text: '"Children are natural scientists."', italic: true },
        { text: ' Why is the sky blue? Where does the rain go after it disappears? What is inside a cloud? Fifty questions a day. Most of them I had no good answer for.' },
      ]},
      'I started Good Atoms because I wanted something to hand him — not another video clip, not another piece of homework, but a real conversation that opened a door in his head.',
      { type: 'mixed' as const, segments: [
        { text: 'The Norwegian system builds free, secure children. It is a gift the world often misreads. But the school day ends at two, and children do not stop wondering then. Good Atoms is for the hours between — for the children who keep asking ' },
        { text: 'why', italic: true },
        { text: ', and for the parents who want something meaningful to build alongside them.' },
      ]},
    ],
    compactBio: 'Founder of Good Atoms. Writing from Oslo, alongside his son Ayaan and fifty questions a day.',
  },
};

type Paragraph = string | { type: 'mixed'; segments: { text: string; italic?: boolean }[] };

function renderParagraph(p: Paragraph, key: number) {
  if (typeof p === 'string') {
    return (
      <p key={key} className="font-body text-ink/80 text-base sm:text-lg leading-relaxed">
        {p}
      </p>
    );
  }
  return (
    <p key={key} className="font-body text-ink/80 text-base sm:text-lg leading-relaxed">
      {p.segments.map((s, i) => s.italic ? <em key={i}>{s.text}</em> : <span key={i}>{s.text}</span>)}
    </p>
  );
}

function PhotoOrFallback({ alt, className }: { alt: string; className: string }) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <div
        className={`${className} flex items-center justify-center bg-teal text-white font-display font-black tracking-tight`}
        role="img"
        aria-label={alt}
      >
        <span className="text-4xl sm:text-5xl">SR</span>
      </div>
    );
  }
  return (
    <img
      src={PHOTO_SRC}
      alt={alt}
      onError={() => setFailed(true)}
      loading="lazy"
      className={`${className} object-cover`}
    />
  );
}

// ─── Bio accordion (full variant only) ───────────────────────────────────────

interface BioAccordionProps {
  /** First paragraph is always visible (the lede). */
  lede: Paragraph;
  /** Everything after is collapsed by default. */
  rest: Paragraph[];
  expandLabel: string;
  collapseLabel: string;
}

function BioAccordion({ lede, rest, expandLabel, collapseLabel }: BioAccordionProps) {
  const [open, setOpen] = useState(false);
  const innerRef = useRef<HTMLDivElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  // First-mount guard so the collapsed start-state is set without any tween.
  const mountedRef = useRef(false);

  useEffect(() => {
    const wrap = wrapRef.current;
    const inner = innerRef.current;
    if (!wrap || !inner) return;

    const reduced = !motionEnabled();
    const gsap = gsapWithScrollTrigger();

    // Measure the natural height of the hidden content.
    const targetHeight = inner.scrollHeight;

    if (!mountedRef.current) {
      // Initial render — snap to collapsed without animation.
      mountedRef.current = true;
      gsap.set(wrap, { height: open ? targetHeight : 0 });
      return;
    }

    if (reduced) {
      // Reduced motion — snap instantly so the user still gets the disclosure.
      gsap.set(wrap, { height: open ? targetHeight : 0 });
      return;
    }

    gsap.killTweensOf(wrap);
    gsap.to(wrap, {
      height: open ? targetHeight : 0,
      duration: DURATION.medium,
      ease: open ? EASE.entrance : EASE.exit,
      onComplete: () => {
        // When open, drop the explicit height so the panel reflows naturally
        // if the viewport changes (font reflow, image loads, etc.).
        if (open) gsap.set(wrap, { height: 'auto' });
      },
    });
  }, [open]);

  return (
    <div className="space-y-4">
      {/* Lede — always visible */}
      {renderParagraph(lede, -1)}

      {/* Collapsible region */}
      <div
        ref={wrapRef}
        id="founder-bio-rest"
        className="overflow-hidden"
        style={{ height: 0 }}
        aria-hidden={!open}
      >
        <div ref={innerRef} className="space-y-4 pt-4">
          {rest.map((p, i) => renderParagraph(p, i))}
        </div>
      </div>

      {/* Toggle */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        aria-controls="founder-bio-rest"
        className="founder-bio-toggle inline-flex items-center gap-1.5 font-display font-bold text-sm text-teal hover:text-teal/80 focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-teal transition-colors"
      >
        <span>{open ? collapseLabel : expandLabel}</span>
        <span
          aria-hidden="true"
          className="inline-block transition-transform duration-300"
          style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
        >
          ↓
        </span>
      </button>
    </div>
  );
}

export function FounderCard({ lang, variant = 'full' }: FounderCardProps) {
  const copy = COPY[lang];

  if (variant === 'compact') {
    return (
      <aside
        className="max-w-3xl mx-auto my-8 p-5 md:p-6 rounded-2xl bg-white border border-gray-100 shadow-sm flex items-start gap-4"
        aria-label={copy.name}
      >
        <PhotoOrFallback alt={copy.photoAlt} className="w-16 h-16 rounded-full shrink-0" />
        <div className="min-w-0">
          <p className="font-display font-semibold text-ink text-base">{copy.name}</p>
          <p className="font-display text-xs text-ink-muted uppercase tracking-wider mt-0.5">
            {copy.title}
          </p>
          <p className="mt-2 font-body text-ink-muted text-sm md:text-base leading-relaxed">
            {copy.compactBio}
          </p>
        </div>
      </aside>
    );
  }

  const [lede, ...rest] = copy.paragraphs as Paragraph[];

  return (
    <section
      className="founder-section py-16 sm:py-24 px-4 sm:px-6 bg-sand"
      aria-labelledby="founder-card-heading"
      style={{ background: '#F5EDD8' }}
    >
      <div className="max-w-5xl mx-auto">
        <p className="text-center text-xs font-extrabold text-teal uppercase tracking-[0.25em] mb-6">
          {copy.eyebrow}
        </p>

        <article className="founder-article grid md:grid-cols-5 gap-8 md:gap-12 items-start bg-white rounded-3xl border border-ink/8 shadow-card p-6 sm:p-10 md:p-12">
          {/* Photo column */}
          <div className="md:col-span-2">
            <div className="founder-photo-frame relative rounded-2xl overflow-hidden aspect-[4/5] md:aspect-[3/4] bg-ink/5">
              <PhotoOrFallback
                alt={copy.photoAlt}
                className="founder-photo absolute inset-0 w-full h-full"
              />
            </div>
            <p className="mt-3 font-display text-xs text-ink-muted uppercase tracking-wider">
              {copy.location}
            </p>
          </div>

          {/* Bio column */}
          <div className="md:col-span-3 space-y-5">
            <header>
              <h2
                id="founder-card-heading"
                className="font-display font-black text-2xl sm:text-3xl text-ink leading-tight"
                style={{ fontSize: 'clamp(1.5rem, 2.6vw, 1.875rem)' }}
              >
                {copy.name}
              </h2>
              <p className="font-display text-sm text-ink-muted mt-1 font-medium">{copy.title}</p>
            </header>

            <BioAccordion
              lede={lede}
              rest={rest}
              expandLabel={copy.expand}
              collapseLabel={copy.collapse}
            />
          </div>
        </article>
      </div>
    </section>
  );
}

export default FounderCard;
